/**
 * Grid Map AI Content Application
 *
 * Pure functions that take AI-generated DungeonContent and apply it to a GridMap,
 * creating entities, triggers, and narrative elements.
 */

import type { GridMap, Entity, Trigger, RoomMetadata, TriggerCondition } from '@omen-quest/grid-map';

// ============================================================================
// TYPES (matches server-side Zod schema output)
// ============================================================================

export interface DungeonContent {
  story: {
    title: string;
    hook: string;
    objective: string;
    objectiveRoomId: number;
  };
  rooms: Array<{
    roomId: number;
    name: string;
    description: string;
    onEnterText: string | null;
  }>;
  encounters: Array<{
    roomId: number;
    type: 'monster' | 'npc';
    name: string;
    description: string;
    dialogue: string | null;
    threatLevel: 'minion' | 'standard' | 'elite' | 'boss' | null;
  }>;
  traps: Array<{
    roomId: number;
    name: string;
    description: string;
    damage: number | null;
    warningText: string | null;
  }>;
  triggers: Array<{
    roomId: number;
    condition: 'step' | 'proximity' | 'visible' | 'interact';
    effectType: 'dialogue' | 'trap' | 'spawn' | 'custom';
    text: string | null;
    speaker: string | null;
    eventName: string | null;
    placement: 'center' | 'door' | 'random' | 'edge';
    oneShot: boolean;
    requiredFlags: string[];
    setsFlag: string | null;
    visible: boolean;
    icon: string | null;
  }>;
  progression: {
    flags: string[];
    sequence: string;
  };
}

export interface RuntimeTriggerResponse {
  narrative: string;
  speaker: string | null;
  stateChanges: {
    setsFlag: string | null;
    spawnEntities: Array<{
      type: 'npc' | 'monster' | 'object';
      name: string;
      roomId: number;
    }>;
  } | null;
}

// ============================================================================
// ROOM LOOKUP
// ============================================================================

export function getRoomAtPosition(rooms: RoomMetadata[], x: number, y: number): RoomMetadata | null {
  for (const room of rooms) {
    if (x >= room.x && x < room.x + room.width &&
        y >= room.y && y < room.y + room.height) {
      return room;
    }
  }
  return null;
}

// ============================================================================
// PLACEMENT HELPERS
// ============================================================================

function findFloorTile(map: GridMap, x: number, y: number): { x: number; y: number } | null {
  if (x >= 0 && x < map.width && y >= 0 && y < map.height) {
    const cell = map.cells[y]?.[x];
    if (cell && cell.terrain === 'floor') return { x, y };
  }
  return null;
}

function findPlacementInRoom(
  map: GridMap,
  room: RoomMetadata,
  placement: 'center' | 'door' | 'random' | 'edge',
  usedPositions: Set<string>,
): { x: number; y: number } | null {
  const key = (x: number, y: number) => `${x},${y}`;

  // Try to avoid positions already used by other entities/triggers
  const isAvailable = (pos: { x: number; y: number } | null) =>
    pos && !usedPositions.has(key(pos.x, pos.y)) ? pos : null;

  switch (placement) {
    case 'center': {
      const cx = room.x + Math.floor(room.width / 2);
      const cy = room.y + Math.floor(room.height / 2);
      const center = isAvailable(findFloorTile(map, cx, cy));
      if (center) return center;
      // Fall through to random if center is taken
      break;
    }
    case 'door': {
      // Look for door tiles adjacent to the room
      for (let x = room.x - 1; x <= room.x + room.width; x++) {
        for (let y = room.y - 1; y <= room.y + room.height; y++) {
          if (x < 0 || x >= map.width || y < 0 || y >= map.height) continue;
          const cell = map.cells[y]?.[x];
          if (cell?.terrain === 'door') {
            // Place just inside the room from the door
            const inwardX = Math.max(room.x, Math.min(room.x + room.width - 1, x));
            const inwardY = Math.max(room.y, Math.min(room.y + room.height - 1, y));
            const pos = isAvailable(findFloorTile(map, inwardX, inwardY));
            if (pos) return pos;
          }
        }
      }
      break;
    }
    case 'edge': {
      // Try tiles along the room perimeter (1 tile inside walls)
      const edgeTiles: Array<{ x: number; y: number }> = [];
      for (let x = room.x; x < room.x + room.width; x++) {
        edgeTiles.push({ x, y: room.y });
        edgeTiles.push({ x, y: room.y + room.height - 1 });
      }
      for (let y = room.y + 1; y < room.y + room.height - 1; y++) {
        edgeTiles.push({ x: room.x, y });
        edgeTiles.push({ x: room.x + room.width - 1, y });
      }
      for (const tile of edgeTiles) {
        const pos = isAvailable(findFloorTile(map, tile.x, tile.y));
        if (pos) return pos;
      }
      break;
    }
  }

  // Fallback: random floor tile in the room
  for (let y = room.y; y < room.y + room.height; y++) {
    for (let x = room.x; x < room.x + room.width; x++) {
      const pos = isAvailable(findFloorTile(map, x, y));
      if (pos) return pos;
    }
  }

  // Last resort: any floor tile, even if occupied
  for (let y = room.y; y < room.y + room.height; y++) {
    for (let x = room.x; x < room.x + room.width; x++) {
      const pos = findFloorTile(map, x, y);
      if (pos) return pos;
    }
  }

  return null;
}

// ============================================================================
// APPLY DUNGEON CONTENT
// ============================================================================

/**
 * Takes a GridMap and AI-generated DungeonContent, returns a new GridMap
 * with entities, triggers, and narrative applied.
 */
export function applyDungeonContent(
  map: GridMap,
  content: DungeonContent,
  rooms: RoomMetadata[],
): GridMap {
  const newEntities: Entity[] = [...map.entities];
  const newTriggers: Trigger[] = [...(map.triggers ?? [])];
  const usedPositions = new Set<string>();
  let idCounter = 0;
  const nextId = (prefix: string) => `ai_${prefix}_${idCounter++}`;

  // Mark existing entity positions as used
  for (const e of map.entities) {
    usedPositions.add(`${e.x},${e.y}`);
  }

  const roomMap = new Map(rooms.map(r => [r.roomId, r]));

  // --- Room-enter triggers ---
  for (const roomDef of content.rooms) {
    if (!roomDef.onEnterText) continue;
    const room = roomMap.get(roomDef.roomId);
    if (!room) continue;

    const pos = findPlacementInRoom(map, room, 'center', usedPositions);
    if (!pos) continue;

    newTriggers.push({
      id: nextId('enter'),
      cells: [pos],
      condition: 'step',
      effect: { type: 'dialogue', text: roomDef.onEnterText, speaker: 'Narrator' },
      oneShot: true,
      enabled: true,
    });
  }

  // --- Encounters ---
  for (const encounter of content.encounters) {
    const room = roomMap.get(encounter.roomId);
    if (!room) continue;

    const pos = findPlacementInRoom(map, room, 'random', usedPositions);
    if (!pos) continue;
    usedPositions.add(`${pos.x},${pos.y}`);

    const entity: Entity = {
      id: nextId(encounter.type),
      x: pos.x,
      y: pos.y,
      type: encounter.type === 'npc' ? 'npc' : 'monster',
      name: encounter.name,
      icon: encounter.type === 'npc' ? '?' : '!',
      metadata: {
        description: encounter.description,
        threatLevel: encounter.threatLevel,
      },
    };
    newEntities.push(entity);

    // NPC dialogue trigger (interact)
    if (encounter.type === 'npc' && encounter.dialogue) {
      newTriggers.push({
        id: nextId('npc_talk'),
        cells: [pos],
        condition: 'interact',
        effect: { type: 'dialogue', text: encounter.dialogue, speaker: encounter.name },
        oneShot: false,
        enabled: true,
        visible: true,
        icon: '?',
      });
    }
  }

  // --- Traps ---
  for (const trap of content.traps) {
    const room = roomMap.get(trap.roomId);
    if (!room) continue;

    const pos = findPlacementInRoom(map, room, 'center', usedPositions);
    if (!pos) continue;
    usedPositions.add(`${pos.x},${pos.y}`);

    // Step trigger for trap activation
    newTriggers.push({
      id: nextId('trap'),
      cells: [pos],
      condition: 'step',
      effect: { type: 'trap', damage: trap.damage ?? undefined, description: trap.description },
      oneShot: true,
      enabled: true,
    });

    // Proximity warning trigger
    if (trap.warningText) {
      newTriggers.push({
        id: nextId('trap_warn'),
        cells: [pos],
        condition: 'proximity',
        radius: 3,
        effect: { type: 'dialogue', text: trap.warningText, speaker: 'Narrator' },
        oneShot: true,
        enabled: true,
      });
    }
  }

  // --- AI-defined triggers ---
  for (const trigDef of content.triggers) {
    const room = roomMap.get(trigDef.roomId);
    if (!room) continue;

    const pos = findPlacementInRoom(map, room, trigDef.placement, usedPositions);
    if (!pos) continue;

    const trigger: Trigger = {
      id: nextId('trig'),
      cells: [pos],
      condition: trigDef.condition as TriggerCondition,
      effect: buildTriggerEffect(trigDef),
      oneShot: trigDef.oneShot,
      enabled: true,
      requiredFlags: trigDef.requiredFlags.length > 0 ? trigDef.requiredFlags : undefined,
      setsFlag: trigDef.setsFlag ?? undefined,
      visible: trigDef.visible || undefined,
      icon: trigDef.icon ?? undefined,
    };

    if (trigDef.condition === 'proximity') {
      trigger.radius = 3;
    }

    newTriggers.push(trigger);
  }

  return {
    ...map,
    name: content.story.title,
    entities: newEntities,
    triggers: newTriggers,
  };
}

function buildTriggerEffect(trigDef: DungeonContent['triggers'][number]): Trigger['effect'] {
  switch (trigDef.effectType) {
    case 'dialogue':
      return {
        type: 'dialogue',
        text: trigDef.text ?? '',
        speaker: trigDef.speaker ?? undefined,
      };
    case 'trap':
      return {
        type: 'trap',
        description: trigDef.text ?? 'A trap springs!',
      };
    case 'spawn':
      return {
        type: 'spawn',
        entities: [],
      };
    case 'custom':
      return {
        type: 'custom',
        eventName: trigDef.eventName ?? 'custom_event',
        data: {
          text: trigDef.text,
          speaker: trigDef.speaker,
        },
      };
  }
}
